import React, {createContext, useContext, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import io from 'socket.io-client';
import html2canvas from 'html2canvas';
import useStore from '../store/store';
const SocketContext = createContext();
import usePersistentAuthStore from '../store/persistentAuthStore';

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({children}) => {
  const [socket, setSocket] = useState(null);
  const {auth} = usePersistentAuthStore();
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  const period = useStore(state => state.period);
  const selection = useStore(state => state.selection);
  const selectedSection = useStore(state => state.selectedSection);

  // Keep refs updated so connect listener always has latest values
  const periodRef = useRef(period);
  const selectionRef = useRef(selection);
  const selectedSectionRef = useRef(selectedSection);

  pathnameRef.current = location.pathname;
  periodRef.current = period;
  selectionRef.current = selection;
  selectedSectionRef.current = selectedSection;

  // Transform selectedSection value to tab name (matches Home.js tab labels)
  const getSelectedSectionName = section => {
    switch (section) {
      case 0:
        return 'Hourly';
      case 1:
        return 'Daily';
      case 2:
        return 'Prices';
      case 3:
        return 'Demand';
      case 4:
        return 'Geo';
      case 5:
        return 'Sources';
      case 'analytics':
        return 'Analytics';
      case 'stacked_demand':
        return 'Demand stack';
      case 'prebid_stack':
        // Conditional: "Prebid stack" if auth.type >= 4, otherwise "Demand stack"
        return auth?.type >= 4 ? 'Prebid stack' : 'Demand stack';
      case 'direct_stack':
        return 'Direct stack';
      case 'prices_report':
        return 'Prices';
      default:
        return section?.toString() || null;
    }
  };

  // Helper to create location message data (uses refs for latest values)
  const getLocationData = () => ({
    type: 'ClientUIuserLocation',
    location: pathnameRef.current,
    selection: selectionRef.current,
    period: periodRef.current,
    selectedSection: pathnameRef.current.includes('home')
      ? getSelectedSectionName(selectedSectionRef.current)
      : null,
    branch: process.env.REACT_APP_CURRENT_BRANCH,
  });

  // Capture a screenshot of the current page (viewport) as compressed JPEG data URL
  const captureScreenshot = async () => {
    try {
      if (typeof document === 'undefined' || !document.body) return null;

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        // Downscale a bit to reduce payload size while keeping it readable
        scale: 0.75,
      });

      // Encode as JPEG with quality 0.7 to significantly reduce payload size
      return canvas.toDataURL('image/jpeg', 0.7);
    } catch (e) {
      console.error('Failed to capture screenshot', e);
      return null;
    }
  };

  useEffect(() => {
    if (auth) {
      const newSocket = io.connect(
        process.env.NODE_ENV === 'development'
          ? ''
          : process.env.REACT_APP_CURRENT_BRANCH === 'master'
            ? 'https://wb.setupad.com'
            : 'https://wb-api-dev.setupad.com',
        {
          secure: true,
          transports: ['polling', 'websocket'],
          reconnectionDelay: 500,
          reconnection: true,
          timeout: 5000,
          reconnectionAttempts: Infinity,
          autoConnect: true,
          withCredentials: true,
          query: auth
            ? {
                token: auth.token,
                accountId: auth.accountId,
                username: auth.username,
              }
            : undefined,
        }
      );

      setSocket(newSocket);

      return () => {
        newSocket.close();
      };
    }
  }, [setSocket, auth]);

  useEffect(() => {
    if (!socket || !socket.connected) return;
    socket.on('connect_error', err => {
      console.log(`Websocket connect_error due to ${err.message}`, err);
    });

    socket.on('error', error => {
      console.log('Websocket error', error);
    });

    return () => {
      if (socket) {
        socket.off('connect_error');
        socket.off('error');
      }
    };
  }, [socket]);

  // Listen for backend "getScreenshot" requests and respond with a screenshot
  useEffect(() => {
    if (!socket) return;

    const handleMsg = async msg => {
      try {
        if (!msg || msg.type !== 'getScreenshot') return;

        const screenshot = await captureScreenshot();
        if (!screenshot) {
          socket.emit('msg', {
            type: 'ClientScreenshotError',
            reason: 'captureFailed',
          });
          return;
        }

        socket.emit('msg', {
          type: 'ClientScreenshot',
          screenshot,
        });
      } catch (error) {
        console.error('Error handling getScreenshot message', error);
      }
    };

    socket.on('msg', handleMsg);

    return () => {
      socket.off('msg', handleMsg);
    };
  }, [socket]);

  // Emit location/selection/period on changes
  useEffect(() => {
    if (!socket?.connected || !auth) return;

    const data = getLocationData();
    socket.emit('msg', data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, location.pathname, selection, period, selectedSection, auth]);

  // Set up reconnect listener (only re-registers when socket/auth changes)
  useEffect(() => {
    if (!socket || !auth) return;

    const emitLocation = () => {
      if (!socket.connected) return;

      const data = getLocationData();
      socket.emit('msg', data);
    };

    // Emit immediately if already connected (handles initial connect)
    if (socket.connected) {
      emitLocation();
    }

    // Listen for reconnects (uses refs, so always has latest values)
    socket.on('connect', emitLocation);
    return () => socket.off('connect', emitLocation);
  }, [socket, auth]);

  return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
};
